import React, { useEffect, useMemo, useRef } from 'react';
import { Modal, Platform, Pressable, ScrollView, View } from 'react-native';
import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import CustomBottomSheet from '../../../components/Reusables/bottomSheet/CustomBottomSheet';
import type { InventoryStyles } from './inventoryStyles';

type Props = {
  styles: InventoryStyles;
  open: boolean;
  onClose: () => void;
  header: React.ReactNode;
  footer?: React.ReactNode;
  /** Native sheet height; the web panel is always full height. */
  snapPoints?: string[];
  /** When true the children scroll themselves (a FlatList). */
  disableDefaultScroll?: boolean;
  children: React.ReactNode;
};

/**
 * Hosts a filter sheet: a bottom sheet on phone and tablet, a right-hand
 * panel on web, with the same header, body and footer in both.
 */
const InventoryPanel: React.FC<Props> = ({
  styles,
  open,
  onClose,
  header,
  footer,
  snapPoints,
  disableDefaultScroll,
  children,
}) => {
  if (Platform.OS === 'web') {
    return (
      <WebSidePanel
        styles={styles}
        open={open}
        onClose={onClose}
        header={header}
        footer={footer}
        disableDefaultScroll={disableDefaultScroll}>
        {children}
      </WebSidePanel>
    );
  }
  return (
    <NativeSheet
      styles={styles}
      open={open}
      onClose={onClose}
      header={header}
      footer={footer}
      snapPoints={snapPoints}
      disableDefaultScroll={disableDefaultScroll}>
      {children}
    </NativeSheet>
  );
};

const ignoreSheetChange = () => {};

const NativeSheet: React.FC<Props> = ({
  styles,
  open,
  onClose,
  header,
  footer,
  snapPoints,
  disableDefaultScroll,
  children,
}) => {
  const ref = useRef<BottomSheetModal>(null);
  const insets = useSafeAreaInsets();
  const points = useMemo(() => snapPoints ?? ['85%'], [snapPoints]);

  useEffect(() => {
    if (open) ref.current?.present();
    else ref.current?.dismiss();
  }, [open]);

  // Closing is read from onDismiss, not from index -1: opening the category
  // picker hides the Filters sheet behind it (index -1) without closing it.
  return (
    <CustomBottomSheet
      bottomSheetModalRef={ref}
      snapPoints={points}
      enableDynamicSizing={false}
      handleSheetChanges={ignoreSheetChange}
      onDismiss={onClose}
      header={header}
      disableDefaultScroll={disableDefaultScroll}
      footer={
        footer ? (
          <View
            style={[
              styles.sheetFooter,
              { paddingBottom: Math.max(insets.bottom, 12) },
            ]}>
            {footer}
          </View>
        ) : undefined
      }>
      {children}
    </CustomBottomSheet>
  );
};

const WebSidePanel: React.FC<Omit<Props, 'snapPoints'>> = ({
  styles,
  open,
  onClose,
  header,
  footer,
  disableDefaultScroll,
  children,
}) => (
  <Modal
    visible={open}
    transparent
    animationType="fade"
    onRequestClose={onClose}>
    <View style={styles.panelBackdrop}>
      <Pressable
        style={styles.panelScrim}
        onPress={onClose}
        accessibilityLabel="Close"
      />
      <View style={styles.panel} accessibilityViewIsModal>
        {header}
        {disableDefaultScroll ? (
          <View style={styles.panelBody}>{children}</View>
        ) : (
          <ScrollView
            style={styles.panelBody}
            keyboardShouldPersistTaps="handled">
            {children}
          </ScrollView>
        )}
        {footer ? (
          <View style={[styles.sheetFooter, styles.panelFooter]}>{footer}</View>
        ) : null}
      </View>
    </View>
  </Modal>
);

export default InventoryPanel;
